import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { ReadMoreButton } from '../../../ui/ReadMoreButton';
import AppActions from 'app/shared/flux/actions/AppActions';
import NewWindowTealIcon from 'images/icons/new-window-teal.svg';
import { container, externalIcon, websiteContainer, websiteLink } from './aboutTheBuildingModule.styles';

// Using progressive enhancement ReadMoreButton for SEO-friendly truncation

/**
 * AboutTheBuildingModule Component
 *
 * Displays building description with progressive enhancement read more/less functionality
 * and property website link with external icon.
 *
 * Features:
 * - SEO-friendly progressive enhancement (content visible without JavaScript)
 * - Accessible read more/less toggle with WCAG compliance
 * - External website link with analytics tracking
 * - Conditional rendering based on data availability
 * - Fetches data directly from Redux (no prop drilling)
 */
export const AboutTheBuildingModule = () => {
  const dispatch = useDispatch();

  // Fetch data directly from Redux state
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  const displayName = currentListing?.displayName;
  const fullDescription = currentListing?.details?.fullDescription;
  const websiteUrl = currentListing?.details?.webSite;
  const websiteDomain = currentListing?.details?.webSiteOriginalDomain;

  // Early return if no data to display
  if (!fullDescription && !websiteUrl) {
    return null;
  }

  // Handle property website click with analytics
  const handleWebsiteClick = () => {
    // Send analytics event
    dispatch(AppActions.sendEventToApi('building_website_visited', null));

    // Track building-specific event if available
    if (currentListing?.maloneLotIdEncoded) {
      dispatch(AppActions.sendEventToApi('building_website_clicked', currentListing.maloneLotIdEncoded));
    }
  };

  // Determine display text for website link — prefer building/apartment name
  const websiteLinkText =
    displayName || (websiteDomain && !isUglyDomain(websiteDomain) ? `Visit ${websiteDomain}` : 'Property website');

  return (
    <div className={container}>
      {fullDescription && (
        <ReadMoreButton
          content={fullDescription}
          maxTruncatedLength={150}
          readMoreText="Read more"
          readLessText="Read less"
          aria-label="Building description"
        />
      )}

      {/* Website Link Section */}
      {websiteUrl && (
        <div className={websiteContainer}>
          <a
            href={websiteUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={handleWebsiteClick}
            className={websiteLink}
            aria-label={displayName ? `Visit ${displayName} website` : `Visit property website: ${websiteUrl}`}
          >
            {websiteLinkText}
            <img src={NewWindowTealIcon} alt="" className={externalIcon} aria-hidden="true" />
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * Determines if a domain should be considered "ugly" and use generic text
 * Based on logic from PpcLink.tsx
 */
function isUglyDomain(domain: string): boolean {
  return domain.split('.').length > 3 || domain.includes('prospectportal') || domain.includes('reslisting');
}
